/**
 * ImportStep Component
 * Step 4: Import MT5 trades to Q AI
 *
 * Features:
 * - Trade preview before import
 * - Progress bar during import
 * - Success celebration with stats
 * - Suggested prompts for Q AI
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    CircularProgress,
    Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorIcon from '@mui/icons-material/Error';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import { importTrades, getTradePreview, getImportProgress } from '../../../../services/qbridge.service';
import { toast } from 'react-toastify';

const ImportStep = ({
    onComplete,
    onBack,
    onClose,
    colors,
    existingImportData = null  // Data from backend about existing imports
}) => {
    const [importStatus, setImportStatus] = useState('preview'); // preview, importing, success, error, already_imported
    const [preview, setPreview] = useState(null);
    const [progress, setProgress] = useState(0);
    const [importResult, setImportResult] = useState(null);
    const [error, setError] = useState(null);

    // Determine initial status based on existing imports
    useEffect(() => {
        if (existingImportData?.hasExistingImport && existingImportData?.tradeCount > 0) {
            // User already has imports - show "already imported" state with option to sync new
            setImportStatus('already_imported');
        }
    }, [existingImportData]);

    // Fetch trade preview on mount (from local QBridge)
    useEffect(() => {
        const fetchPreview = async () => {
            const result = await getTradePreview();
            if (result.success) {
                setPreview(result);
            }
        };
        fetchPreview();
    }, []);

    // Handle import
    const handleImport = async () => {
        setImportStatus('importing');
        setProgress(0);
        setError(null);

        // Start progress animation (simulated while waiting for API)
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) return prev;
                return prev + Math.random() * 10;
            });
        }, 500);

        const result = await importTrades();

        clearInterval(progressInterval);

        if (result.success) {
            setProgress(100);
            setImportResult(result);
            setImportStatus('success');
            toast.success(`Successfully imported ${result.imported} trades!`);

            // Notify parent with full result object
            setTimeout(() => {
                onComplete({
                    imported: result.imported || 0,
                    skipped: result.skipped || 0,
                    tokensUsed: result.tokensUsed || 0
                });
            }, 2000);
        } else {
            setError(result.error || 'Import failed. Please try again.');
            setImportStatus('error');
            toast.error(result.error || 'Import failed');
        }
    };

    // Handle retry
    const handleRetry = () => {
        setImportStatus('preview');
        setProgress(0);
        setError(null);
    };

    // Calculate new trades available (if any)
    const newTradesAvailable = preview?.tradeCount && existingImportData?.tradeCount
        ? Math.max(0, preview.tradeCount - existingImportData.tradeCount)
        : existingImportData?.newTradesAvailable || null;

    // Format last import date
    const formatLastImportDate = (dateStr) => {
        if (!dateStr) return 'Unknown';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Render based on status
    if (importStatus === 'already_imported') {
        return (
            <Box>
                {/* Heading */}
                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1rem',
                        fontWeight: 600,
                        mb: 0.5
                    }}
                >
                    Trades Already Synced
                </Typography>
                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.8125rem',
                        mb: 3
                    }}
                >
                    Your MT5 history is connected to Q AI
                </Typography>

                {/* Existing Import Summary Card */}
                <Box
                    sx={{
                        background: colors.primaryLight,
                        border: `1px solid ${colors.primaryBorder}`,
                        borderRadius: '12px',
                        padding: '20px',
                        mb: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CheckCircleIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: colors.text,
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    lineHeight: 1
                                }}
                            >
                                {existingImportData?.tradeCount || 0}
                            </Typography>
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.9375rem', lineHeight: 1.5 }}>
                                trades already imported
                            </Typography>
                        </Box>
                    </Box>

                    {/* Last import info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textSecondary }}>
                        <HistoryIcon sx={{ fontSize: '1rem' }} />
                        <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                            Last synced: {formatLastImportDate(existingImportData?.lastImportAt)}
                        </Typography>
                    </Box>

                    {/* Account info if available */}
                    {existingImportData?.broker && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: colors.textMuted }}>
                            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                                {existingImportData.broker} • Account {existingImportData.accountNumber}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* New trades available notice */}
                {newTradesAvailable > 0 && (
                    <Box
                        sx={{
                            background: colors.infoLight,
                            border: `1px solid rgba(59, 130, 246, 0.3)`,
                            borderRadius: '10px',
                            padding: '12px 16px',
                            mb: 3
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.info,
                                fontSize: '0.875rem',
                                fontWeight: 600
                            }}
                        >
                            <SyncIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                            {newTradesAvailable} new trades available to sync
                        </Typography>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    {/* Sync New Trades Button */}
                    <Button
                        onClick={handleImport}
                        fullWidth
                        startIcon={<SyncIcon />}
                        sx={{
                            background: newTradesAvailable > 0
                                ? `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`
                                : colors.surface,
                            border: newTradesAvailable > 0 ? 'none' : `1px solid ${colors.border}`,
                            color: newTradesAvailable > 0 ? '#FFFFFF' : colors.text,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            padding: '14px 24px',
                            borderRadius: '12px',
                            boxShadow: newTradesAvailable > 0 ? `0 4px 16px ${colors.primaryLight}` : 'none',
                            '&:hover': {
                                boxShadow: newTradesAvailable > 0 ? `0 6px 24px rgba(39, 181, 109, 0.4)` : 'none',
                                background: newTradesAvailable > 0
                                    ? `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`
                                    : colors.surfaceHover
                            }
                        }}
                    >
                        {newTradesAvailable > 0 ? `Sync ${newTradesAvailable} New Trades` : 'Sync All Trades'}
                    </Button>
                </Box>

                {/* Done Button */}
                <Button
                    onClick={onClose}
                    fullWidth
                    sx={{
                        background: colors.surface,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: '14px 24px',
                        borderRadius: '12px',
                        mb: 2,
                        '&:hover': {
                            background: colors.surfaceHover
                        }
                    }}
                >
                    Done - Start Chatting with Q
                </Button>

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                        onClick={onBack}
                        sx={{
                            color: colors.textMuted,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: colors.surfaceHover }
                        }}
                    >
                        ← Back
                    </Button>
                </Box>
            </Box>
        );
    }

    if (importStatus === 'success') {
        return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
                {/* Celebration Icon */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        boxShadow: `0 0 60px ${colors.primaryLight}`,
                        animation: 'bounce 0.6s ease-out'
                    }}
                >
                    <CelebrationIcon sx={{ color: '#FFFFFF', fontSize: '2.5rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.375rem',
                        fontWeight: 700,
                        mb: 1
                    }}
                >
                    You're All Set!
                </Typography>

                <Typography
                    sx={{
                        color: colors.primary,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        mb: 3
                    }}
                >
                    {importResult?.imported || 0} trades imported
                </Typography>

                {/* Stats Summary */}
                <Box
                    sx={{
                        background: colors.surface,
                        border: `1px solid ${colors.borderLight}`,
                        borderRadius: '12px',
                        padding: '16px',
                        mb: 3
                    }}
                >
                    <Typography
                        sx={{
                            color: colors.textSecondary,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            mb: 1.5,
                            lineHeight: 1.4
                        }}
                    >
                        What you can ask Q now:
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <SuggestionChip text={'"Analyze my trading performance"'} colors={colors} />
                        <SuggestionChip text={'"What\'s my win rate on EURUSD?"'} colors={colors} />
                        <SuggestionChip text={'"When do I trade best?"'} colors={colors} />
                        <SuggestionChip text={'"Review my EA strategies"'} colors={colors} />
                    </Box>
                </Box>

                {/* CTA Button */}
                <Button
                    onClick={onClose}
                    fullWidth
                    sx={{
                        background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        boxShadow: `0 4px 16px ${colors.primaryLight}`,
                        '&:hover': {
                            boxShadow: `0 6px 24px rgba(39, 181, 109, 0.4)`
                        }
                    }}
                >
                    Start Chatting with Q
                </Button>
            </Box>
        );
    }

    if (importStatus === 'importing') {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                {/* Import Animation */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: colors.primaryLight,
                        border: `2px solid ${colors.primary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        animation: 'pulse 1.5s infinite'
                    }}
                >
                    <CloudUploadIcon sx={{ color: colors.primary, fontSize: '2rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        mb: 1
                    }}
                >
                    Importing Your Trades
                </Typography>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.9375rem',
                        mb: 3,
                        lineHeight: 1.5
                    }}
                >
                    This may take a moment...
                </Typography>

                {/* Progress Bar */}
                <Box sx={{ px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: colors.textSecondary, fontSize: '0.875rem', lineHeight: 1.5 }}>
                            Progress
                        </Typography>
                        <Typography sx={{ color: colors.primary, fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 }}>
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: colors.borderLight,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: `linear-gradient(90deg, ${colors.primary} 0%, #22C55E 100%)`
                            }
                        }}
                    />
                </Box>
            </Box>
        );
    }

    if (importStatus === 'error') {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                {/* Error Icon */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: colors.errorLight,
                        border: `2px solid ${colors.error}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}
                >
                    <ErrorIcon sx={{ color: colors.error, fontSize: '2rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.125rem',
                        fontWeight: 700,
                        mb: 1
                    }}
                >
                    Import Failed
                </Typography>

                <Typography
                    sx={{
                        color: colors.error,
                        fontSize: '0.9375rem',
                        mb: 3,
                        px: 2,
                        lineHeight: 1.5
                    }}
                >
                    {error}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        onClick={handleRetry}
                        sx={{
                            background: colors.surface,
                            border: `1px solid ${colors.border}`,
                            color: colors.text,
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '10px 24px',
                            borderRadius: '10px',
                            '&:hover': { background: colors.surfaceHover }
                        }}
                    >
                        Try Again
                    </Button>
                </Box>

                <Button
                    onClick={onBack}
                    sx={{
                        color: colors.textMuted,
                        textTransform: 'none',
                        mt: 2,
                        '&:hover': { color: colors.textSecondary }
                    }}
                >
                    ← Go Back
                </Button>
            </Box>
        );
    }

    // Default: Preview state
    return (
        <Box>
            {/* Heading */}
            <Typography
                sx={{
                    color: colors.text,
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 0.5
                }}
            >
                Step 4: Import Your Trades
            </Typography>
            <Typography
                sx={{
                    color: colors.textMuted,
                    fontSize: '0.9375rem',
                    mb: 3,
                    lineHeight: 1.5
                }}
            >
                Sync your MT5 trade history to Q AI
            </Typography>

            {/* Trade Preview Card */}
            <Box
                sx={{
                    background: colors.surface,
                    border: `1px solid ${colors.borderLight}`,
                    borderRadius: '12px',
                    padding: '20px',
                    mb: 3
                }}
            >
                {preview ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    background: colors.primaryLight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <ShowChartIcon sx={{ color: colors.primary, fontSize: '1.5rem' }} />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: colors.text,
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        lineHeight: 1
                                    }}
                                >
                                    {preview.tradeCount}
                                </Typography>
                                <Typography sx={{ color: colors.textMuted, fontSize: '0.9375rem', lineHeight: 1.5 }}>
                                    trades found
                                </Typography>
                            </Box>
                        </Box>

                        {preview.dateRange && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textMuted }}>
                                <CalendarTodayIcon sx={{ fontSize: '1rem' }} />
                                <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                                    {preview.dateRange.from} to {preview.dateRange.to}
                                </Typography>
                            </Box>
                        )}

                        {preview.summary && (
                            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                {preview.summary.winRate && (
                                    <StatChip
                                        label="Win Rate"
                                        value={`${preview.summary.winRate}%`}
                                        colors={colors}
                                    />
                                )}
                                {preview.summary.totalPnL && (
                                    <StatChip
                                        label="Total P/L"
                                        value={`$${preview.summary.totalPnL}`}
                                        colors={colors}
                                    />
                                )}
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} sx={{ color: colors.primary }} />
                        <Typography sx={{ color: colors.textMuted, fontSize: '0.9375rem', lineHeight: 1.5 }}>
                            Loading trade preview...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Import Button */}
            <Button
                onClick={handleImport}
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                    color: '#FFFFFF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    boxShadow: `0 4px 16px ${colors.primaryLight}`,
                    mb: 3,
                    '&:hover': {
                        boxShadow: `0 6px 24px rgba(39, 181, 109, 0.4)`
                    }
                }}
            >
                Import Trades
            </Button>

            {/* Info Note */}
            <Box
                sx={{
                    background: colors.infoLight,
                    border: `1px solid rgba(59, 130, 246, 0.3)`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    mb: 3
                }}
            >
                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.9375rem',
                        lineHeight: 1.5
                    }}
                >
                    <TrendingUpIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    Q AI will analyze your trading patterns and provide personalized insights
                </Typography>
            </Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                    onClick={onBack}
                    sx={{
                        color: colors.textSecondary,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: colors.surfaceHover }
                    }}
                >
                    ← Back
                </Button>
            </Box>
        </Box>
    );
};

// Suggestion Chip Component
const SuggestionChip = ({ text, colors }) => (
    <Typography
        sx={{
            color: colors.textMuted,
            fontSize: '0.9375rem',
            fontStyle: 'italic',
            padding: '8px 12px',
            background: colors.surfaceHover,
            borderRadius: '8px',
            lineHeight: 1.5
        }}
    >
        {text}
    </Typography>
);

// Stat Chip Component
const StatChip = ({ label, value, colors }) => (
    <Box
        sx={{
            background: colors.primaryLight,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '8px',
            padding: '8px 12px',
            textAlign: 'center'
        }}
    >
        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mb: 0.25, lineHeight: 1.4 }}>
            {label}
        </Typography>
        <Typography sx={{ color: colors.primary, fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 }}>
            {value}
        </Typography>
    </Box>
);

export default ImportStep;
